/**
 * LE CA MOYEN PAR MOIS (et le panier moyen) — 3, 6 ou 12 mois contre la même
 * durée juste avant.
 *
 * LES RÈGLES D'AMINE (10 octobre 2026), qui remplacent celles du premier jet :
 *   1. on compte les factures payées, impayées, en retard ET les brouillons ;
 *      les annulées, non ;
 *   2. au mois de leur DATE DE FACTURATION — ni l'échéance, ni l'encaissement ;
 *   3. le mois EN COURS est inclus, même entamé : au 10 octobre, « 3 mois »
 *      veut dire août, septembre, octobre ;
 *   4. la moyenne divise par le nombre de mois de la fenêtre ;
 *   5. la comparaison prend la même durée juste avant, mêmes règles.
 * Son jeu de contrôle, Eco Delivery Urban au 10 octobre 2026 : 22 644 +
 * 30 060 + 47 256 = 99 960 € sur 3 mois, soit 33 320 €/mois (test
 * « ca-moyen.test.ts »). Le premier jet, sans brouillons ni mois en cours,
 * affichait 15 596 €.
 *
 * Les factures validées comptent pour leur solde, avoirs déduits — la règle de
 * « construireTableauDeBord » ; les brouillons pour leur TTC.
 */
import { construireTableauDeBord, type BornesPeriode } from '@/lib/rapports';

export interface FenetrePanier {
  debut: Date;
  fin: Date;
  caFacture: number;
  nbFactures: number;
  /** Facturé ÷ nombre de factures ; « null » s'il n'y en a aucune. */
  panierMoyen: number | null;
  /** Facturé ÷ nombre de mois de la fenêtre. */
  caMensuelMoyen: number;
}

/** Les durées proposées à l'écran (Amine, 10 octobre 2026 : « 3 mois aussi, et 12 pour voir l'évolution »). */
export const DUREES_PANIER = [3, 6, 12] as const;
export type DureePanier = (typeof DUREES_PANIER)[number];

export interface PanierMoyen {
  nbMois: number;
  recent: FenetrePanier;
  precedent: FenetrePanier;
  /** Évolution du panier moyen en %, « null » si la fenêtre précédente est vide. */
  evolution: number | null;
  /**
   * Évolution du CA mensuel moyen en %, « null » sans CA avant. C'est le
   * chiffre qu'Amine regarde : « le CA moyen m'importe plus, pas le panier ».
   */
  evolutionCA: number | null;
}

const arrondir = (n: number) => Math.round(n * 100) / 100;

/**
 * Les deux fenêtres : les « nbMois » derniers mois, mois en cours COMPRIS
 * jusqu'à sa fin, puis la même durée juste avant.
 */
export function fenetresDuPanier(maintenant: Date, nbMois: number = 6): { recent: BornesPeriode; precedent: BornesPeriode } {
  const an = maintenant.getFullYear();
  const mois = maintenant.getMonth();
  return {
    recent: { debut: new Date(an, mois - nbMois + 1, 1), fin: new Date(an, mois + 1, 0, 23, 59, 59, 999) },
    precedent: { debut: new Date(an, mois - 2 * nbMois + 1, 1), fin: new Date(an, mois - nbMois + 1, 0, 23, 59, 59, 999) },
  };
}

function fenetre(documents: any[], bornes: BornesPeriode, maintenant: Date, nbMois: number): FenetrePanier {
  const { synthese } = construireTableauDeBord(documents, bornes, maintenant);
  // Validées (solde, avoirs déduits) + brouillons (TTC) ; annulées exclues.
  const ca = arrondir(synthese.caFacture + synthese.brouillons);
  const nb = synthese.nbValidees + synthese.nbBrouillons;
  return {
    debut: bornes.debut,
    fin: bornes.fin,
    caFacture: ca,
    nbFactures: nb,
    panierMoyen: nb > 0 ? arrondir(ca / nb) : null,
    caMensuelMoyen: arrondir(ca / nbMois),
  };
}

export function calculerPanierMoyen(documents: any[], maintenant: Date, nbMois: number = 6): PanierMoyen {
  const f = fenetresDuPanier(maintenant, nbMois);
  const recent = fenetre(documents, f.recent, maintenant, nbMois);
  const precedent = fenetre(documents, f.precedent, maintenant, nbMois);
  const evolution =
    recent.panierMoyen !== null && precedent.panierMoyen !== null && precedent.panierMoyen > 0
      ? Math.round(((recent.panierMoyen - precedent.panierMoyen) / precedent.panierMoyen) * 1000) / 10
      : null;
  const evolutionCA =
    precedent.caMensuelMoyen > 0
      ? Math.round(((recent.caMensuelMoyen - precedent.caMensuelMoyen) / precedent.caMensuelMoyen) * 1000) / 10
      : null;
  return { nbMois, recent, precedent, evolution, evolutionCA };
}
